/**
 * 官网公告 / Changelog 数据层。
 *
 * 数据来自后端公开读接口 GET /api/app/announcements（oran-ai，@SaIgnore，与 material-square 同
 * 经 /api/app 代理转发）。返回当前生效的已发布公告，置顶优先、日期倒序。
 * 接口失败时静默返回空数组，不阻塞官网（公告栏/更新日志不渲染）。
 *
 * 组件用 useAnnouncements() 钩子（react-query）异步取数；getAnnouncements()/latestAnnouncement()
 * 保留为最近一次成功结果的同步快照。
 */
import { useQuery } from "@tanstack/react-query";

export type Lang = "zh" | "en";

export interface Announcement {
  id: string;
  /** 展示日期（ISO date，如 2026-06-10） */
  date: string;
  /** 置顶（公告栏取置顶中最新一条；changelog 中置顶项标星） */
  pinned?: boolean;
  title: Record<Lang, string>;
  summary: Record<Lang, string>;
  /** 标准 changelog markdown（双语） */
  bodyMd: Record<Lang, string>;
}

/** 后端公开读返回项（扁平双语字段，见 ToolsAnnouncementResp） */
interface AnnouncementApiItem {
  id: number | string;
  titleZh?: string;
  titleEn?: string;
  summaryZh?: string;
  summaryEn?: string;
  bodyMdZh?: string;
  bodyMdEn?: string;
  pinned?: boolean;
  publishAt?: string | number[] | null;
}

const ENDPOINT = "/api/app/announcements";

function toDateString(publishAt: AnnouncementApiItem["publishAt"]): string {
  if (typeof publishAt === "string") return publishAt.slice(0, 10);
  // 兼容 Jackson 把 LocalDateTime 序列化为 [year,month,day,...] 数组的情况
  if (Array.isArray(publishAt) && publishAt.length >= 3) {
    const [y, m, d] = publishAt;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  return "";
}

function mapItem(it: AnnouncementApiItem): Announcement {
  return {
    id: String(it.id),
    date: toDateString(it.publishAt),
    pinned: Boolean(it.pinned),
    title: { zh: it.titleZh ?? "", en: it.titleEn ?? "" },
    summary: { zh: it.summaryZh ?? "", en: it.summaryEn ?? "" },
    bodyMd: { zh: it.bodyMdZh ?? "", en: it.bodyMdEn ?? "" },
  };
}

/** 最近一次成功结果的同步快照（供同步调用方读取）。 */
let cache: Announcement[] = [];

/** 拉取公告；任何失败都静默返回空数组，不抛错、不阻塞官网。 */
export async function fetchAnnouncements(): Promise<Announcement[]> {
  try {
    const res = await fetch(ENDPOINT, { headers: { Accept: "application/json" } });
    if (!res.ok) return [];
    const json = await res.json();
    // 兼容 R 包装 { data: [...] } 与裸数组
    const raw: unknown = json && typeof json === "object" && "data" in json ? (json as { data: unknown }).data : json;
    const list: AnnouncementApiItem[] = Array.isArray(raw) ? (raw as AnnouncementApiItem[]) : [];
    cache = list.map(mapItem);
    return cache;
  } catch {
    return [];
  }
}

/** 全部公告（同步快照；首次渲染前为空，拉到后填充）。 */
export function getAnnouncements(): Announcement[] {
  return cache;
}

/** 最新一条公告（同步快照，置顶优先由后端排序保证）。 */
export function latestAnnouncement(): Announcement | undefined {
  return cache[0];
}

/** 异步取数钩子（react-query）。组件优先用它。 */
export function useAnnouncements() {
  const { data, isLoading } = useQuery({
    queryKey: ["site-announcements"],
    queryFn: fetchAnnouncements,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const announcements = data ?? [];
  return { announcements, latest: announcements[0] as Announcement | undefined, isLoading };
}
